/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { OperationStatus } from "Constants/OperationStatus";
import { AppState } from "Dataflows/Reducers";
import { IAwardKitsBenefitDetail } from "Interfaces/Benefits/IAwardKitsBenefitDetail";
import { IM365BenefitDetail } from "Interfaces/Benefits/IM365BenefitDetail";
import { IMicrosoft365BenefitDetail } from "Interfaces/Benefits/IMicrosoft365BenefitDetail";
import { IProfileBenefitResult } from "Interfaces/Benefits/IProfileBenefitResult";
import { ITeamsBenefitDetail } from "Interfaces/Benefits/ITeamsBenefitDetail";
import { IOperationStatusType } from "Interfaces/IOperationStatusType";
import { IMVPAzureBenefitClaim } from "Interfaces/MVP/IMVPAzureBenefitClaim";

/**
 * Returns the getProfileBenefits value.
 * @param {AppState} state
 * @returns {IProfileBenefitResult}
 */
export const getProfileBenefits = (state: AppState): IProfileBenefitResult =>
  state.mvpState.benefitsState.profileBenefits;

/**
 * Returns loading status of user profile comments.
 * @param appState
 * @returns {boolean}
 */
export const getProfileBenefitsStatus = (state: AppState): OperationStatus | undefined =>
  state.mvpState.benefitsState.benefitsOperationStatus?.read;

/**
 * Returns loading status of user profile comments.
 * @param appState
 * @returns {boolean}
 */
export const getAddCommentOperationStatus = (state: AppState): OperationStatus | undefined =>
  state.mvpState.benefitsState.benefitsOperationStatus?.create;

/**
 * Returns error message returned when user profile comments fails.
 * @param appState
 * @returns {string}
 */
export const getProfileBenefitsErrorMessage = (state: AppState): string =>
  state.mvpState.benefitsState.benefitsOperationStatus?.read === OperationStatus.Failed &&
  !!state.mvpState.benefitsState.profileBenefitsErrorMessage
    ? state.mvpState.benefitsState.profileBenefitsErrorMessage
    : "";

/**
 * Returns the isClaimingBenefitSuccess value.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const getIsClaimingBenefitStatus = (state: AppState): OperationStatus | undefined =>
  state.mvpState.benefitsState.benefitsOperationStatus?.create;

/**
 * Returns the isClaimingBenefitError value.
 * @param {AppState} state
 * @returns {string}
 */
export const getIsClaimingBenefitsError = (state: AppState): string =>
  state.mvpState.benefitsState.benefitsOperationStatus?.create === OperationStatus.Failed &&
  !!state.mvpState.benefitsState.claimBenefitError
    ? state.mvpState.benefitsState.claimBenefitError
    : "";

/**
 * Gets the M365 Benefit Detail from state.
 * @param {AppState} state
 * @returns {IM365BenefitDetail}
 */
export const m365GetBenefitDetail = (state: AppState): IM365BenefitDetail =>
  state.mvpState.m365BenefitDetailState.m365BenefitDetail;

/**
 * Gets the M365 Update Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const m365UpdateStatus = (state: AppState): OperationStatus | undefined =>
  state.mvpState.m365BenefitDetailState.m365OperationStatus?.update;

/**
 * Gets the M365 Fetch Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const m365FetchStatus = (state: AppState): OperationStatus | undefined =>
  state.mvpState.m365BenefitDetailState.m365OperationStatus?.read;

/**
 * Gets the M365 Fetch Error message.
 * @param {AppState} state
 * @returns {string}
 */
export const m365FetchErrorMessage = (state: AppState): string =>
  state.mvpState.m365BenefitDetailState.m365FetchErrorMessage;

/**
 * Gets the M365 Update Error message
 * @param {AppState} state
 * @returns {string}
 */
export const m365UpdateErrorMessage = (state: AppState): string =>
  state.mvpState.m365BenefitDetailState.m365UpdateErrorMessage;

/**
 * Gets the MS365 Benefit Detail from state.
 * @param {AppState} state
 * @returns {IMicrosoft365BenefitDetail}
 */
export const ms365GetBenefitDetail = (state: AppState): IMicrosoft365BenefitDetail =>
  state.mvpState.ms365BenefitDetailState.ms365BenefitDetail;

/**
 * Gets the MS365 Fetch Error message.
 * @param {AppState} state
 * @returns {string}
 */
export const ms365FetchErrorMessage = (state: AppState): string =>
  state.mvpState.ms365BenefitDetailState.ms365FetchErrorMessage;

/**
 * Gets the MS365 Update Error message
 * @param {AppState} state
 * @returns {string}
 */
export const ms365UpdateErrorMessage = (state: AppState): string =>
  state.mvpState.ms365BenefitDetailState.ms365UpdateErrorMessage;

/**
 * Gets the MS365 Fetch Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const ms365FetchStatus = (state: AppState): OperationStatus | undefined =>
  state.mvpState.ms365BenefitDetailState.ms365OperationStatus?.read;

/**
 * Gets the MS365 Update Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const ms365UpdateStatus = (state: AppState): OperationStatus | undefined =>
  state.mvpState.ms365BenefitDetailState.ms365OperationStatus?.update;

/**
 * Gets the M365 Benefit Detail from state.
 * @param {AppState} state
 * @returns {ITeamsBenefitDetail}
 */
export const teamsGetBenefitDetail = (state: AppState): ITeamsBenefitDetail =>
  state.mvpState.teamsBenefitDetailState.teamsBenefitDetail;

/**
 * Gets the Award Kits Benefit Detail from state.
 * @param {AppState} state
 * @returns {IAwardKitsBenefitDetail}
 */
export const awardKitsGetBenefitDetail = (state: AppState): IAwardKitsBenefitDetail =>
  state.mvpState.awardKitsBenefitDetailState.awardKitsBenefitDetail;

/**
 * Gets the Teams Fetch Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const teamsFetchStatus = (state: AppState): OperationStatus | undefined =>
  state.mvpState.teamsBenefitDetailState.teamsOperationStatus?.read;

/**
 * Gets the Award Kits Fetch Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const awardKitsFetchStatus = (state: AppState): OperationStatus | undefined =>
  state.mvpState.awardKitsBenefitDetailState.awardKitsOperationStatus?.read;

/**
 * Gets the Teams Fetch Error message.
 * @param {AppState} state
 * @returns {string}
 */
export const teamsFetchErrorMessage = (state: AppState): string =>
  state.mvpState.teamsBenefitDetailState.teamsFetchErrorMessage;

/**
 * Gets the Award Kits Fetch Error message.
 * @param {AppState} state
 * @returns {string}
 */
export const awardKitsFetchErrorMessage = (state: AppState): string =>
  state.mvpState.awardKitsBenefitDetailState.awardKitsFetchErrorMessage;

export const azureBenefitUpdateStatus = (state: AppState): OperationStatus | undefined =>
  state.mvpState.azureBenefitExtendCreditState.extendAzureCreditOperationStatus?.update;

export const azureBenefitUpdateErrorMessage = (state: AppState): string =>
  state.mvpState.azureBenefitExtendCreditState.extendAzureCreditError;

/**
 * Returns azure benefit.
 * @param appState
 */
export const getAddAzureBenefit = (state: AppState): IMVPAzureBenefitClaim | undefined =>
  state.mvpState.azureBenefitAddState.azureBenefit;

export const getAddAzureBenefitOperationStatus = (state: AppState): IOperationStatusType =>
  state.mvpState.azureBenefitAddState.addAzureBenefitOperationStatus;

/**
 * Returns save status of azure benefit.
 * @param appState
 * @returns {boolean}
 */
export const getAddAzureBenefitStatus = (state: AppState): boolean =>
  state.mvpState.azureBenefitAddState.addAzureBenefitOperationStatus?.update ===
  OperationStatus.Pending;

/**
 * Returns error message azure benefit fails.
 * @param appState
 * @returns {string}
 */
export const getAddAzureBenefitErrorMessage = (state: AppState): string =>
  state?.mvpState?.azureBenefitAddState?.addAzureBenefitError || "";
